import { createContext, useContext, useMemo } from "react";
import { useAppSharedState } from "hooks/useAppSharedState";
import { useMsalAppSession } from "hooks/useMsalAppSession";
import { useMsalSessionWakeGuard } from "hooks/useMsalSessionWakeGuard";

const AppContext = createContext(null);

export const AppContextProvider = ({
  children,
  profile,
  subscriptionStatus,
  getProfile,
  getSubscriptionStatus,
  getCompanyInformation,
}) => {
  const {
    account,
    instance,
    isAuthenticated,
    msalStartupPending,
    msalAccountRecovering,
  } = useMsalAppSession();

  // Re-verify auth after device sleep without duplicating axios / lifecycle refresh.
  useMsalSessionWakeGuard({ msalStartupPending });

  const sharedState = useAppSharedState({
    profile,
    subscriptionStatus,
    getProfile,
    getSubscriptionStatus,
    getCompanyInformation,
    account,
    instance,
    isAuthenticated,
    msalStartupPending,
  });

  const value = useMemo(
    () => ({
      ...sharedState,
      account,
      isAuthenticated,
      msalStartupPending,
      msalAccountRecovering,
    }),
    [sharedState, account, isAuthenticated, msalStartupPending, msalAccountRecovering],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppContextProvider");
  }
  return context;
};
